import { useState } from 'react';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import api from '@/services/apiService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/shadcn/dialog';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { Button } from '@/components/shadcn/button';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

interface CommunityMenuPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommunityMenuPasswordDialog({ open, onOpenChange }: CommunityMenuPasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      showToast.error('Kata sandi saat ini wajib diisi');
      return;
    }
    if (newPassword.length < 6) {
      showToast.error('Kata sandi baru minimal 6 karakter');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast.error('Konfirmasi kata sandi baru tidak cocok');
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await api.post('/community/update-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      showToast.success('Kata sandi berhasil diperbarui!');
      onOpenChange(false);
      reset();
    } catch (err) {
      showToast.error(getApiErrorMessage(err, 'Gagal memperbarui kata sandi'));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl p-6 bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg font-extrabold text-[#0E1726] flex items-center gap-2">
            <Icon icon="ph:lock-key-bold" className="w-5 h-5 text-[#FC480D]" />
            <span>Ganti Password Akun</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleUpdatePassword} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-extrabold text-gray-700">Kata Sandi Saat Ini</Label>
            <Input
              type="password"
              placeholder="Masukkan kata sandi saat ini"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="rounded-xl text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-extrabold text-gray-700">Kata Sandi Baru</Label>
            <Input
              type="password"
              placeholder="Minimal 6 karakter"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="rounded-xl text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-extrabold text-gray-700">Konfirmasi Kata Sandi Baru</Label>
            <Input
              type="password"
              placeholder="Ulangi kata sandi baru"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="rounded-xl text-xs"
            />
          </div>
          <DialogFooter className="pt-2 flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isUpdatingPassword}
              className="rounded-xl text-xs font-bold"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={isUpdatingPassword}
              className="bg-[#FC480D] hover:bg-[#e03e09] text-white rounded-xl text-xs font-bold gap-1.5"
            >
              {isUpdatingPassword ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
